import React, { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import Sidebar from "./Sidebar";
import MobileTopBar from "./MobileTopBar";
import { ThemeProvider, useTheme } from "../context/ThemeContext";
import AccountChip from "./AccountChip";
import AmbientSyncIndicator from "./AmbientSyncIndicator";
import { ActiveSyncJobsProvider } from "../hooks/useActiveSyncJobs";
import { useRecentSliceRefresh } from "../hooks/useRecentSliceRefresh";
import { ConnectGmailModalProvider } from "../context/ConnectGmailModalContext";
import { useAppBootstrap } from "../context/AppBootstrapContext";
import MailboxLimitBlockedCallout from "./MailboxLimitBlockedCallout";
import ColorLegend from "./ColorLegend";
import { domainsApi, type DomainConfig } from "../api/domains";
import type { MailboxLimitDetail } from "../utils/mailboxLimitMessaging";
import {
  MAILBOX_LIMIT_CODE,
  parseMailboxOAuthSearch,
  stripMailboxOAuthQueryParams,
} from "../utils/mailboxLimitMessaging";
import {
  cancellationScheduledLine,
  formatAccessUntilLabel,
  getSubscriptionSnapshot,
  isTrialEndingSoon,
  trialEndedEffectiveFreeLine,
  trialEndingSoonBannerLine,
  trialEndingSoonDaysLeft,
} from "../utils/commercialPlanMessaging";

// Global keyboard shortcuts for navigation
const NAV_SHORTCUTS: Record<string, string> = {
  't': '/app/today',
  'w': '/app/workbench',
  'a': '/app/archive',
  'd': '/app/documents',
  'i': '/app/statistics',
  's': '/app/profile',
  'b': '/app/billing',
  '?': '/app/help',
};

const LayoutContent: React.FC = () => {
  useRecentSliceRefresh();
  const { isDarkMode, toggleTheme } = useTheme();
  const { bootstrap } = useAppBootstrap();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  // The menu's Color legend (phones). It opens over any page, so the shell owns this dialog
  // and loads the area list when it opens; Today's desktop button keeps Today's own dialog.
  const [colorLegendOpen, setColorLegendOpen] = useState(false);
  const [legendDomains, setLegendDomains] = useState<DomainConfig[] | undefined>(undefined);
  const openColorLegend = useCallback(() => {
    setColorLegendOpen(true);
    domainsApi
      .getDomains()
      .then(setLegendDomains)
      .catch(() => {
        // The legend falls back to the built-in areas.
      });
  }, []);
  // Same breakpoint as Tailwind `lg` and the Sidebar. Below it the account chip joins the
  // mobile top bar, so the shell renders it once and it stays on screen while the page scrolls.
  const isDesktop = useMediaQuery("(min-width: 1024px)", { noSsr: true });
  const [mailboxRouteNotice, setMailboxRouteNotice] = useState<
    | { type: 'limit'; detail: MailboxLimitDetail }
    | { type: 'generic'; message: string }
    | null
  >(null);
  const [dismissMailboxNotice, setDismissMailboxNotice] = useState(false);
  const handledMailboxQueryKeyRef = useRef<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const newState = !prev;
      try {
        localStorage.setItem('sidebar_collapsed', String(newState));
      } catch (e) {
        // Ignore storage errors
      }
      return newState;
    });
  };

  const subscriptionSnapshot = getSubscriptionSnapshot(bootstrap);
  const trialDaysLeft = trialEndingSoonDaysLeft(subscriptionSnapshot);
  const showTrialBanner = isTrialEndingSoon(subscriptionSnapshot);
  const trialEndedLine = trialEndedEffectiveFreeLine(bootstrap?.user?.plan, bootstrap?.user?.effective_plan);
  const showCancellationBanner = Boolean(
    subscriptionSnapshot?.tier === 'pro' &&
    subscriptionSnapshot?.cancel_at_period_end,
  );
  const accessUntil = subscriptionSnapshot?.access_until || subscriptionSnapshot?.cancel_at;
  const accessUntilLabel = formatAccessUntilLabel(accessUntil);
  const cancellationLine = cancellationScheduledLine(accessUntilLabel);

  useEffect(() => {
    const search = location.search;
    if (!search || search.length <= 1) {
      handledMailboxQueryKeyRef.current = null;
      setMailboxRouteNotice(null);
      setDismissMailboxNotice(false);
      return;
    }
    const queryKey = `${location.pathname}${search}`;
    if (handledMailboxQueryKeyRef.current === queryKey) {
      return;
    }
    const parsed = parseMailboxOAuthSearch(search);
    if (!parsed) {
      return;
    }
    handledMailboxQueryKeyRef.current = queryKey;

    if ('code' in parsed && parsed.code === MAILBOX_LIMIT_CODE) {
      setMailboxRouteNotice({ type: 'limit', detail: parsed });
      setDismissMailboxNotice(false);
    } else if ('genericMessage' in parsed) {
      setMailboxRouteNotice({ type: 'generic', message: parsed.genericMessage });
      setDismissMailboxNotice(false);
    }
    // Do not strip query here: child routes (e.g. TodayPage → ConnectGmailModal) read
    // `mailbox` / `auth` in an effect that runs after this parent effect; clearing the URL
    // first would hide structured limit state from the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- URL segments only
  }, [location.search, location.pathname, location.hash]);

  const dismissMailboxRouteNotice = () => {
    setDismissMailboxNotice(true);
    const nextSearch = stripMailboxOAuthQueryParams(location.search);
    if (nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }
  };

  // Global keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input, textarea, or contenteditable
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }

      // Ignore if modifier keys are pressed (except shift for ?)
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = e.key.toLowerCase();
      const path = NAV_SHORTCUTS[key];

      if (path) {
        e.preventDefault();
        navigate(path);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const skipToMain = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById('app-main-content')?.focus();
  };

  // Today renders at /app (index) and /app/today. Below 1024 px its card's sync button says
  // what the indicator would, so the indicator keeps only its live region there.
  const quietSync = !isDesktop && /^\/app(\/today)?\/?$/.test(location.pathname);

  return (
    <ConnectGmailModalProvider>
    <div
      className="flex min-h-screen font-sans flex-col lg:flex-row"
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      {/* First tab stop — before chrome (UX-DR27) */}
      <a
        href="#app-main-content"
        onClick={skipToMain}
        className="fixed left-4 top-4 z-[1250] rounded-lg border px-4 py-2 text-sm font-semibold shadow-lg outline-none transition-opacity pointer-events-none opacity-0 focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{
          borderColor: 'var(--color-border)',
          backgroundColor: 'var(--color-bg-secondary)',
          color: 'var(--color-text-primary)',
          outlineColor: 'var(--color-accent)',
        }}
      >
        Skip to main content
      </a>

      {/* Mobile Top Bar */}
      <MobileTopBar
        onOpenSidebar={() => setIsMobileNavOpen(true)}
        trailing={isDesktop ? null : <AmbientSyncIndicator quiet={quietSync} />}
        theme={isDesktop ? undefined : { isDarkMode, onToggle: toggleTheme }}
        account={isDesktop ? null : <AccountChip />}
      />

      <Sidebar
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        isCollapsed={isCollapsed}
        toggleCollapse={toggleCollapse}
        onOpenColorLegend={openColorLegend}
      />
      <ColorLegend
        open={colorLegendOpen}
        onClose={() => setColorLegendOpen(false)}
        domains={legendDomains}
      />

      {/* Main Content Area */}
      <main
        id="app-main-content"
        tabIndex={-1}
        className={`flex-1 min-w-0 p-0 overflow-y-auto h-[calc(100vh-64px)] lg:h-screen scrollbar-modern transition-all duration-200 ease-out ${isCollapsed ? 'lg:ml-16' : 'lg:ml-[220px]'
          }`}
        style={{ backgroundColor: 'var(--color-bg-primary)' }}
      >
        <div className="w-full">
          {/* Sticky chip bar — stays at top on scroll, minimal height (desktop; phones use the top bar) */}
          {isDesktop && (
            <div className="sticky top-0 z-30 flex items-center justify-end gap-2 px-4 lg:px-6 py-2 min-w-0"
                 style={{ backgroundColor: 'var(--color-bg-primary)' }}
            >
              <AmbientSyncIndicator />
              <AccountChip />
            </div>
          )}
          {mailboxRouteNotice && !dismissMailboxNotice && (
            <div className="px-4 lg:px-6 py-2 space-y-2">
              {mailboxRouteNotice.type === 'limit' ? (
                <MailboxLimitBlockedCallout
                  body={mailboxRouteNotice.detail.message}
                  detail={mailboxRouteNotice.detail}
                  usageLine={
                    mailboxRouteNotice.detail.limit != null && mailboxRouteNotice.detail.connected != null
                      ? `${mailboxRouteNotice.detail.connected} of ${mailboxRouteNotice.detail.limit} mailboxes connected on your plan`
                      : null
                  }
                />
              ) : (
                <div
                  className="rounded-xl border border-amber-500/30 bg-amber-500/10 text-theme text-xs px-4 py-2 flex flex-wrap items-center justify-between gap-3"
                  role="status"
                >
                  <span>Gmail connection: {mailboxRouteNotice.message}</span>
                  <button
                    type="button"
                    onClick={() => dismissMailboxRouteNotice()}
                    className="px-3 py-1 rounded-lg border border-amber-500/40 text-theme font-semibold text-[11px] hover:bg-amber-500/10 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {mailboxRouteNotice.type === 'limit' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => dismissMailboxRouteNotice()}
                    className="text-[11px] text-theme-muted hover:text-theme-secondary px-2 py-1"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          )}
          {trialEndedLine && (
            <div className="px-4 lg:px-6 py-2">
              <div
                className="rounded-xl border border-slate-500/30 bg-slate-500/10 text-theme text-xs px-4 py-2 flex items-center justify-between gap-3"
                role="status"
              >
                <span>{trialEndedLine}</span>
                <button
                  type="button"
                  onClick={() => navigate('/app/billing')}
                  className="px-3 py-1 rounded-lg bg-slate-200 text-slate-900 font-semibold text-[11px] hover:bg-white transition-colors"
                >
                  Open Billing
                </button>
              </div>
            </div>
          )}
          {showTrialBanner && typeof trialDaysLeft === 'number' && (
            <div className="px-4 lg:px-6 py-2">
              <div
                className="rounded-xl border border-amber-500/30 bg-amber-500/10 text-theme text-xs px-4 py-2 flex items-center justify-between gap-3"
                role="status"
              >
                <span>{trialEndingSoonBannerLine(trialDaysLeft)}</span>
                <button
                  type="button"
                  onClick={() => navigate('/app/billing')}
                  className="px-3 py-1 rounded-lg bg-amber-500 text-black font-semibold text-[11px] hover:bg-amber-400 transition-colors"
                >
                  Open Billing
                </button>
              </div>
            </div>
          )}
          {showCancellationBanner && (
            <div className="px-4 lg:px-6 py-2">
              <div
                className="rounded-xl border border-sky-500/30 bg-sky-500/10 text-theme text-xs px-4 py-2 flex items-center justify-between gap-3"
                role="status"
              >
                <span>{cancellationLine}</span>
                <button
                  type="button"
                  onClick={() => navigate('/app/billing')}
                  className="px-3 py-1 rounded-lg bg-sky-400 text-slate-950 font-semibold text-[11px] hover:bg-sky-300 transition-colors"
                >
                  Open Billing
                </button>
              </div>
            </div>
          )}
          <Outlet />
        </div>
      </main>
    </div>
    </ConnectGmailModalProvider>
  );
};

const Layout: React.FC = () => {
  return (
    <ThemeProvider>
      <ActiveSyncJobsProvider>
        <LayoutContent />
      </ActiveSyncJobsProvider>
    </ThemeProvider>
  );
};

export default Layout;
